import { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate } from 'react-router-dom';
import FormSRTVisita from '../../components/FormSRTVisita';
import { VisitaEstado } from '../../consts/visitasStatus';
import { useRoles } from '../../../../hooks/useRoles';
import { useGetSiniestroByIdQuery } from '../../../../services/roamApi';

const SRT_EDITABLE_STATES = [
    VisitaEstado.PENDIENTE,
    VisitaEstado.PROGRAMADA,
    VisitaEstado.EN_PROCESO
];

const buildSiniestroForForm = (
    slimSiniestro: any,
    datosSiniestro: any,
    provinciaLugarFallback?: any
) => {
    if (!datosSiniestro) return slimSiniestro;

    const localidadLugar = datosSiniestro.lugarOcurrencia?.localidad;
    // GET /visitas/{idVisita}/siniestro no manda `provincia` dentro de lugarOcurrencia.localidad
    // (a diferencia de GET /roam/siniestros/{id}, que sí la trae completa para el mismo dato).
    // Sin esto, el Autocomplete de Provincia de "Lugar del accidente" queda vacío aunque Ciudad y
    // Código Postal se completen bien (no dependen de ese dato). provinciaLugarFallback llega
    // resuelto por FormularioSRTPage contra el endpoint de ROAM cuando hace falta.
    const lugarOcurrencia =
        localidadLugar && !localidadLugar.provincia && provinciaLugarFallback
            ? {
                  ...datosSiniestro.lugarOcurrencia,
                  localidad: { ...localidadLugar, provincia: provinciaLugarFallback }
              }
            : datosSiniestro.lugarOcurrencia ?? slimSiniestro?.lugarOcurrencia;

    return {
        ...slimSiniestro,
        nroRoam: datosSiniestro.nroRoam ?? slimSiniestro?.nroRoam,
        fechaOcurrrencia: datosSiniestro.fechaOcurrencia,
        relatoHecho: datosSiniestro.relatoHecho,
        empleadorRazonSocial: datosSiniestro.empleadorRazonSocial,
        cuitEmpleador: datosSiniestro.cuitEmpleador,
        lugarOcurrencia,
        accidentado: {
            nombre: datosSiniestro.nombre,
            apellido: datosSiniestro.apellido,
            cuil: datosSiniestro.cuil,
            fechaNacimiento: datosSiniestro.fechaNacimiento,
            datosContacto: {
                direccion: datosSiniestro.domicilio,
                telefonoCelular: datosSiniestro.telefonoCelular,
                telefonoPrincipal: datosSiniestro.telefonoPrincipal,
            }
        }
    };
};

const FormularioSRTPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const selectedVisita = location.state?.selectedVisita;
    const datosSiniestro = location.state?.datosSiniestro ?? null;
    const { isProvider, isViewerBO, isPreventor } = useRoles();

    const lugarLocalidad = datosSiniestro?.lugarOcurrencia?.localidad;
    const faltaProvinciaLugar = Boolean(lugarLocalidad) && !lugarLocalidad?.provincia;
    const idSiniestroRoam = selectedVisita?.siniestro?.id;
    // GET /visitas/{idVisita}/siniestro no trae localidad.provincia de "Lugar del accidente" (ver
    // buildSiniestroForForm). Como fallback, se pide el mismo siniestro por GET
    // /roam/siniestros/{id}, que sí la trae completa, y solo cuando hace falta.
    const { data: roamSiniestroData } = useGetSiniestroByIdQuery(Number(idSiniestroRoam), {
        skip: !idSiniestroRoam || !faltaProvinciaLugar
    });
    const roamLugarLocalidad = roamSiniestroData?.lugarOcurrencia?.localidad;
    const provinciaLugarFallback =
        roamLugarLocalidad?.id === lugarLocalidad?.id ? roamLugarLocalidad?.provincia : null;

    // `FormSRT` resetea Provincia/Ciudad del empleador (y otros selectores locales) cada vez que
    // cambia la referencia de `siniestro` (ver `useEffect(..., [siniestro])` en FormSRT/index.tsx).
    // Sin memoizar, `buildSiniestroForForm` arma un objeto nuevo en cada render de esta página y
    // ese reset se dispara todo el tiempo, dejando esos campos en blanco pese a que el contrato del
    // empleador sí se resuelve bien. Se calcula antes del guard de abajo para no romper las Rules
    // of Hooks (el guard puede retornar antes de llegar a un hook declarado después).
    const siniestro = useMemo(
        () => buildSiniestroForForm(selectedVisita?.siniestro, datosSiniestro, provinciaLugarFallback),
        [selectedVisita?.siniestro, datosSiniestro, provinciaLugarFallback]
    );

    if (!selectedVisita?.id || !selectedVisita?.siniestro?.id) {
        navigate(-1);
        return null;
    }

    const readOnly =
        isProvider || isViewerBO || !SRT_EDITABLE_STATES.includes(selectedVisita.estado);
    const siniestroLabel =
        siniestro?.nroRoam ||
        siniestro?.numero ||
        siniestro?.nroSiniestro ||
        siniestro?.id;

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    variant="outlined"
                    onClick={() => navigate(-1)}
                >
                    Volver
                </Button>
                <Typography variant="h5" component="h1">
                    Formulario SRT
                    {siniestroLabel ? ` - Siniestro ${siniestroLabel}` : ''}
                </Typography>
            </Box>

            <FormSRTVisita
                idVisita={selectedVisita.id}
                readOnly={readOnly}
                siniestro={siniestro}
                onSaveSuccess={() => navigate(-1)}
                lockAutocompletedFields={isPreventor}
            />
        </Box>
    );
};

export default FormularioSRTPage;
